import React, { useEffect, useState, useMemo } from 'react';
import { Table, message, Image, Typography, Tag, Input, Space, Card, Button, Tooltip, Modal } from 'antd';
import { SearchOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import axios from 'axios';

const { Text } = Typography;

interface EcardData {
  celebrationCategoryId: string;
  celebrationCategoryName: string;
  description: string;
  ecardId: string;
  ecardName: string;
  image: string;
}

const ViewEcards: React.FC = () => {
    const [ecardData, setEcardData] = useState<EcardData[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState<string>('');
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
    });
    const { instance, accounts } = useMsal();

    // Filter data based on search text
    const filteredData = useMemo(() => {
        if (!searchText.trim()) {
            return ecardData;
        }

        const searchLower = searchText.toLowerCase();
        return ecardData.filter((ecard) =>
            ecard.ecardName.toLowerCase().includes(searchLower) ||
            ecard.description.toLowerCase().includes(searchLower) ||
            ecard.celebrationCategoryName.toLowerCase().includes(searchLower)
        );
    }, [ecardData, searchText]);

    // Handle pagination changes
    const handleTableChange = (paginationConfig: TablePaginationConfig) => {
        setPagination({
            current: paginationConfig.current || 1,
            pageSize: paginationConfig.pageSize || 50,
        });
    };

    // Reset pagination when search changes
    useEffect(() => {
        setPagination(prev => ({ ...prev, current: 1 }));
    }, [searchText]);

    // Handle delete button click
    const handleDelete = (record: EcardData) => {
        Modal.confirm({
            title: 'Delete Ecard',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to delete this record?`,
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => deleteEcard(record),
            onCancel: () => {
                console.log('Delete cancelled');
            },
        });
    };

    // Delete ecard function
    const deleteEcard = async (record: EcardData) => {
        try {
            const token = await fetchToken();
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/CelebrationEcards/${record.ecardId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            // Remove the deleted ecard from local state
            setEcardData(prevData => prevData.filter(ecard => ecard.ecardId !== record.ecardId));
            
            message.success(`Ecard deleted successfully!`);
        } catch (error) {
            console.error('Error deleting ecard:', error);
            message.error('Failed to delete ecard. Please try again.');
        }
    };

    // Fetch authentication token
    const fetchToken = async () => {
        const res = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0]
        });
        return res.idToken;
    };

    useEffect(() => {
        // Future implementation for fetching and displaying ecards will go here
        fetchEcards();
    }, []);

    const fetchEcards = async () => {
        setLoading(true);
        try {
            const token = await fetchToken();
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/CelebrationEcards/get_celebration_cards_category`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setEcardData(response.data || []);
        }
        catch (error) {
            console.error('Error fetching ecards:', error);
            message.error('Failed to fetch ecards');
        } finally {
            setLoading(false);
        }
    };

    // Define table columns
    const columns: ColumnsType<EcardData> = [
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            width: 100,
            render: (image: string, record: EcardData) => (
                <Image
                    width={60}
                    height={60}
                    src={`data:image/jpeg;base64,${image}`}
                    alt={record.ecardName}
                    style={{ objectFit: 'cover', borderRadius: '4px' }}
                    fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1xkE8M+Q6JJOoCOYDqAjGI4gOIIhCIYiGIpgCIIhGIZiGIpgKIKhCIYiGIphKIYhCIYiGI5gOALhCAQjEIxAOALhCAQjEIxAMALhCIQjEIxAMALBCIQjEIxAMALhCAT/A" // placeholder base64 image
                />
            ),
        },
        {
            title: 'Ecard Name',
            dataIndex: 'ecardName',
            key: 'ecardName',
            sorter: (a: EcardData, b: EcardData) => a.ecardName.localeCompare(b.ecardName),
            render: (text: string) => <Text strong>{text}</Text>,
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            sorter: (a: EcardData, b: EcardData) => a.description.localeCompare(b.description),
            ellipsis: true,
        },
        {
            title: 'Category',
            dataIndex: 'celebrationCategoryName',
            key: 'celebrationCategoryName',
            sorter: (a: EcardData, b: EcardData) => a.celebrationCategoryName.localeCompare(b.celebrationCategoryName),
            render: (text: string) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 100,
            fixed: 'right',
            render: (_, record: EcardData) => (
                <Tooltip title="Delete Ecard">
                    <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                        size="small"
                    />
                </Tooltip>
            ),
        },
    ];

    return (
        <div style={{ padding: '20px' }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Card>
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                        <div>
                            <Input
                                placeholder="Search by ecard name, description, or category..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                allowClear
                                size="large"
                                style={{ marginTop: '8px' }}
                            />
                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                Showing {filteredData.length} of {ecardData.length} ecards
                            </Text>
                        </div>
                    </Space>
                </Card>
                
                <Table<EcardData>
                    columns={columns}
                    dataSource={filteredData}
                    loading={loading}
                    rowKey="ecardId"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: filteredData.length,
                        showSizeChanger: true,
                        showQuickJumper: false,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} ecards`,
                        pageSizeOptions: ['5', '10', '20', '50', '100'],
                        onShowSizeChange: (current, size) => {
                            setPagination({ current: 1, pageSize: size });
                        },
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 800 }}
                    size="middle"
                />
            </Space>
        </div>
    );
};

export default ViewEcards;